import { createEmptyScene } from './schema';
import { migrateFieldConfigToConstraints } from './fieldConfigMigration';
import { gcpImageStreamUrl, normalizeKonvaImageNode } from '../api/gcpImagesApi';

let nodeIdSeq = 0;
function nextId(prefix) {
  nodeIdSeq += 1;
  return `${prefix}_${nodeIdSeq}`;
}

function fabricColorToCss(fill) {
  if (!fill) return '#111827';
  if (typeof fill === 'string') return fill;
  return '#111827';
}

/** Legacy Fabric `font` string: e.g. "normal normal 12px \"Times New Roman\"" */
function parseLegacyFabricFont(o) {
  const out = {};
  const font = typeof o.font === 'string' ? o.font.trim() : '';
  if (font) {
    const px = font.match(/(\d+(?:\.\d+)?)px/i);
    if (px) out.fontSize = Number(px[1]);
    const fam = font.match(/px\s+(.+)$/i) || font.match(/["']([^"']+)["']/);
    if (fam) out.fontFamily = fam[1].trim();
    if (/\bbold\b/i.test(font)) out.fontWeight = 'bold';
    if (/\bitalic\b/i.test(font)) out.fontStyle = 'italic';
  }
  return out;
}

function typographyFromFabricJson(o) {
  const legacy = parseLegacyFabricFont(o);
  return {
    fontFamily: o.fontFamily || legacy.fontFamily || 'Arial',
    fontSize: Number(o.fontSize) || legacy.fontSize || 12,
    fontWeight: o.fontWeight || legacy.fontWeight || 'normal',
    fontStyle: o.fontStyle || legacy.fontStyle || 'normal',
    lineHeight: Number(o.lineHeight) > 0 ? Number(o.lineHeight) : 1.16,
    letterSpacing: Number(o.charSpacing) || 0,
    textAlign: o.textAlign || 'left',
    fill: fabricColorToCss(o.fill),
    underline: Boolean(o.underline),
  };
}

function boundingRectFromJson(o) {
  const sx = Math.abs(Number(o.scaleX) || 1);
  const sy = Math.abs(Number(o.scaleY) || 1);
  let w;
  let h;
  if (o.type === 'circle') {
    const r = (Number(o.radius) || 50) * Math.max(sx, sy);
    w = r * 2;
    h = r * 2;
  } else if (o.type === 'line' || o.type === 'polyline') {
    const x1 = Number(o.x1) || 0;
    const y1 = Number(o.y1) || 0;
    const x2 = Number(o.x2) ?? 100;
    const y2 = Number(o.y2) ?? 0;
    w = Math.max(1, Math.abs(x2 - x1) * sx);
    h = Math.max(1, Math.abs(y2 - y1) * sy);
  } else {
    w = Math.max(1, (Number(o.width) || 100) * sx);
    h = Math.max(1, (Number(o.height) || 40) * sy);
  }
  let left = Number(o.left) || 0;
  let top = Number(o.top) || 0;
  const ox = o.originX === 'center' ? w / 2 : o.originX === 'right' ? w : 0;
  const oy = o.originY === 'center' ? h / 2 : o.originY === 'bottom' ? h : 0;
  return { left: left - ox, top: top - oy, width: w, height: h };
}

function transformFromJson(o) {
  const br = boundingRectFromJson(o);
  return {
    x: br.left,
    y: br.top,
    width: br.width,
    height: br.height,
    rotation: Number(o.angle) || 0,
    scaleX: 1,
    scaleY: 1,
    originX: 'left',
    originY: 'top',
    opacity: o.opacity ?? 1,
  };
}

function isTextFabricType(type) {
  return type === 'textbox' || type === 'i-text' || type === 'text';
}

function pushTextNode(scene, o, clip) {
  scene.objects.push({
    id: o.id || nextId('text'),
    type: 'text',
    zIndex: scene.objects.length,
    fieldKey: o.fieldKey,
    fieldConfig: o.fieldConfig,
    text: String(o.text ?? ''),
    transform: transformFromJson(o),
    typography: typographyFromFabricJson(o),
    constraints: migrateFieldConfigToConstraints(o.fieldConfig),
    clip,
    fabricType: o.type,
    locked: o.selectable === false,
  });
}

function pushImageNode(scene, o) {
  const key = String(o.objectKey || o.gcpObjectKey || '').trim();
  const src = key ? gcpImageStreamUrl(key) : (o.src || '');
  scene.objects.push(
    normalizeKonvaImageNode({
      id: o.id || nextId('img'),
      type: 'image',
      zIndex: scene.objects.length,
      fieldKey: o.fieldKey,
      fieldConfig: o.fieldConfig,
      src,
      objectKey: key || undefined,
      gcpObjectKey: key || undefined,
      transform: transformFromJson(o),
      fabricType: 'image',
      locked: o.selectable === false,
    }),
  );
}

function pushShapeNode(scene, o, shapeId) {
  const id = shapeId || o.id || nextId('shape');
  scene.objects.push({
    id,
    type: 'shape',
    zIndex: scene.objects.length,
    fieldKey: o.fieldKey,
    fill: fabricColorToCss(o.fill),
    stroke: typeof o.stroke === 'string' ? o.stroke : undefined,
    strokeWidth: Number(o.strokeWidth) || 0,
    strokeDashArray: o.strokeDashArray,
    transform: transformFromJson(o),
    fabricType: o.type,
    radius: o.radius,
    points: o.points,
    locked: o.selectable === false,
  });
  return id;
}

function walkFabricJsonObject(scene, o, clipRegionId) {
  if (!o || !o.type) return;

  if (isTextFabricType(o.type)) {
    pushTextNode(scene, o, clipRegionId ? { regionId: clipRegionId, shapeType: 'rect' } : undefined);
    return;
  }

  if (o.type === 'image') {
    pushImageNode(scene, o);
    return;
  }

  if (o.type === 'group') {
    const isShapeLabel = !!(o._shapeLabelGroup);
    const isTextBox = !!(o._textBoxGroup);
    const children = Array.isArray(o.objects) ? o.objects : [];
    if (isTextBox) {
      const labelPart = children.find((c) => isTextFabricType(c.type));
      if (labelPart) pushTextNode(scene, labelPart);
      return;
    }
    if (isShapeLabel) {
      const shapePart = children.find((c) => !isTextFabricType(c.type)) || children[0];
      const labelPart = children.find((c) => isTextFabricType(c.type));
      let regionId = clipRegionId;
      if (shapePart) regionId = pushShapeNode(scene, shapePart);
      if (labelPart) {
        pushTextNode(scene, labelPart, {
          regionId: regionId || nextId('clip'),
          shapeType: 'rect',
        });
      }
      return;
    }
    for (const child of children) walkFabricJsonObject(scene, child, clipRegionId);
    return;
  }

  if (o.type === 'rect') {
    if (o._textBoxFrame) return;
    pushShapeNode(scene, o);
    return;
  }

  if (['circle', 'triangle', 'polygon', 'path', 'ellipse', 'line', 'polyline'].includes(o.type)) {
    pushShapeNode(scene, o);
  }
}

/** Serialized Fabric canvas JSON → scene graph for Konva editor. */
export function fabricJsonToSceneGraph(fabricJson, options = {}) {
  nodeIdSeq = 0;
  const w = Math.max(1, options.width || fabricJson?.width || 800);
  const h = Math.max(1, options.height || fabricJson?.height || 600);
  const scene = createEmptyScene(w, h);
  if (typeof fabricJson?.background === 'string') scene.background = fabricJson.background;
  const objects = fabricJson?.objects;
  if (!Array.isArray(objects)) return scene;
  for (const o of objects) walkFabricJsonObject(scene, o, null);
  return scene;
}
